import {
    Component,
    OnInit,
    OnChanges,
    SimpleChanges,
    Input,
    Output,
    EventEmitter,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ElementRef,
    ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ArcGISFeatureService, ArcGISWebMap, ArcGISMapService, ArcGISPaginatedSearchResult } from 'app/shared/models/gis-service-list';
import { AssetItem, AssetsSelectionEvent } from 'app/shared/models/arcgis-types';
import { ArcGISUserService } from 'app/shared/services/arcgis-user-service';
import { ARCGIS_CONSTANTS } from 'app/shared/constants/arcgis-constants';

type ArcGISAsset = ArcGISFeatureService | ArcGISWebMap | ArcGISMapService;

@Component({
    selector: 'app-arcgis-assets-dropdown',
    templateUrl: './arcgis-assets-dropdown.component.html',
    styleUrls: ['./arcgis-assets-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArcgisAssetsDropdownComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public organizationId: string = '';
    @Input() public organizationName: string = '';
    @Input() public preSelectedAssets: Map<string, AssetItem> = new Map();
    @Output() public assetsSelected = new EventEmitter<AssetsSelectionEvent>();
    @Output() public close = new EventEmitter<void>();

    @ViewChild('dropdownPanel', { static: false }) dropdownPanel: ElementRef<HTMLDivElement>;
    @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;
    @ViewChild('assetsList', { static: false }) assetsList: ElementRef<HTMLDivElement>;

    public searchTerm: string = '';
    public autoPlotOnMap: boolean = false;

    public fetchedAssets: AssetItem[] = [];
    public displayAssets: AssetItem[] = [];
    public selectedAssetsMap: Map<string, AssetItem> = new Map();

    public isLoading: boolean = false;
    public isLoadingMore: boolean = false;
    public total: number = 0;
    public hasMore: boolean = true;

    private currentStart: number = 1;
    private currentSearchTerm: string = '';
    private pageSize: number = 200;

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        private elementRef: ElementRef,
        private arcgisUserService: ArcGISUserService
    ) {}

    public get hasAnyAssets(): boolean {
        return this.displayAssets.length > 0 || this.selectedAssetsMap.size > 0;
    }

    public get isInitialLoading(): boolean {
        return this.isLoading && this.fetchedAssets.length === 0 && this.selectedAssetsMap.size === 0;
    }

    ngOnInit(): void {
        this.setupSearchDebounce();
        this.initializeFromPreSelected();

        if (this.organizationId) {
            this.loadInitialAssets();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organizationId']) {
            if (!this.organizationId) {
                this.clearAllState();
            } else if (!changes['organizationId'].firstChange) {
                this.resetAndReload();
            }
        }

        if (changes['preSelectedAssets']) {
            this.initializeFromPreSelected();
            this.updateDisplayAssets();
        }
    }

    private clearAllState(): void {
        this.fetchedAssets = [];
        this.displayAssets = [];
        this.selectedAssetsMap.clear();
        this.searchTerm = '';
        this.currentSearchTerm = '';
        this.currentStart = 1;
        this.hasMore = true;
        this.total = 0;
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupSearchDebounce(): void {
        this.searchSubject.pipe(
            debounceTime(400),
            takeUntil(this.destroy$)
        ).subscribe(searchTerm => {
            this.performSearch(searchTerm);
        });
    }

    private initializeFromPreSelected(): void {
        this.selectedAssetsMap = new Map(this.preSelectedAssets);
    }

    private async loadInitialAssets(): Promise<void> {
        this.isLoading = true;
        this.currentStart = 1;
        this.currentSearchTerm = '';
        this.fetchedAssets = [];
        this.cdr.markForCheck();

        try {
            const result = await this.arcgisUserService.searchOrganizationContent({
                orgId: this.organizationId,
                searchTerm: '',
                start: 1,
                pageSize: this.pageSize
            });

            this.processSearchResult(result, true);
        } catch (error) {
            console.error('Error loading initial assets:', error);
        } finally {
            this.isLoading = false;
            this.cdr.markForCheck();
        }
    }

    private async performSearch(searchTerm: string): Promise<void> {
        this.isLoading = true;
        this.currentStart = 1;
        this.currentSearchTerm = searchTerm;
        this.fetchedAssets = [];
        this.cdr.markForCheck();

        try {
            const result = await this.arcgisUserService.searchOrganizationContent({
                orgId: this.organizationId,
                searchTerm: searchTerm,
                start: 1,
                pageSize: this.pageSize
            });

            this.processSearchResult(result, true);
        } catch (error) {
            console.error('Error searching assets:', error);
        } finally {
            this.isLoading = false;
            this.cdr.markForCheck();
        }
    }

    private async loadMoreAssets(): Promise<void> {
        if (this.isLoadingMore || !this.hasMore) {
            return;
        }

        this.isLoadingMore = true;
        this.cdr.markForCheck();

        try {
            const result = await this.arcgisUserService.searchOrganizationContent({
                orgId: this.organizationId,
                searchTerm: this.currentSearchTerm,
                start: this.currentStart,
                pageSize: this.pageSize
            });

            this.processSearchResult(result, false);
        } catch (error) {
            console.error('Error loading more assets:', error);
        } finally {
            this.isLoadingMore = false;
            this.cdr.markForCheck();
        }
    }

    private processSearchResult(result: ArcGISPaginatedSearchResult, isNewSearch: boolean): void {
        const newAssets = result.assets.map(asset => this.convertToAssetItem(asset));

        if (isNewSearch) {
            this.fetchedAssets = newAssets;
        } else {
            this.fetchedAssets = [...this.fetchedAssets, ...newAssets];
        }

        this.total = result.total;
        this.hasMore = result.hasMore;
        this.currentStart = result.nextStart;

        this.updateDisplayAssets();
        this.cdr.markForCheck();
    }

    private convertToAssetItem(asset: ArcGISAsset): AssetItem {
        let assetType: typeof ARCGIS_CONSTANTS.ASSET_TYPES[keyof typeof ARCGIS_CONSTANTS.ASSET_TYPES];
        let typeLabel: string;

        if (asset.type === ARCGIS_CONSTANTS.SERVICE_TYPES.FEATURE_SERVICE) {
            assetType = ARCGIS_CONSTANTS.ASSET_TYPES.FEATURE_SERVICE;
            typeLabel = ARCGIS_CONSTANTS.ASSET_TYPE_LABELS.FEATURE_SERVICE;
        } else if (asset.type === ARCGIS_CONSTANTS.SERVICE_TYPES.WEB_MAP) {
            assetType = ARCGIS_CONSTANTS.ASSET_TYPES.WEB_MAP;
            typeLabel = ARCGIS_CONSTANTS.ASSET_TYPE_LABELS.WEB_MAP;
        } else if (asset.type === ARCGIS_CONSTANTS.SERVICE_TYPES.MAP_SERVICE) {
            assetType = ARCGIS_CONSTANTS.ASSET_TYPES.MAP_SERVICE;
            typeLabel = ARCGIS_CONSTANTS.ASSET_TYPE_LABELS.MAP_SERVICE;
        } else {
            assetType = ARCGIS_CONSTANTS.ASSET_TYPES.FEATURE_SERVICE;
            typeLabel = ARCGIS_CONSTANTS.ASSET_TYPE_LABELS.FEATURE_SERVICE;
        }

        const isSelected = this.selectedAssetsMap.has(asset.id);

        return {
            id: asset.id,
            name: asset.title,
            type: assetType,
            typeLabel: typeLabel,
            url: asset.url,
            selected: isSelected,
            owner: asset.owner,
            modified: asset.modified,
            access: asset.access,
            contentStatus: asset.contentStatus,
            origin: asset.origin
        };
    }

    private updateDisplayAssets(): void {
        const fetchedIds = new Set(this.fetchedAssets.map(a => a.id));

        const selectedNotInFetched: AssetItem[] = [];
        this.selectedAssetsMap.forEach((asset, id) => {
            if (!fetchedIds.has(id)) {
                selectedNotInFetched.push({ ...asset, selected: true });
            }
        });

        const fetchedWithSelection = this.fetchedAssets.map(asset => ({
            ...asset,
            selected: this.selectedAssetsMap.has(asset.id)
        }));

        this.displayAssets = [...selectedNotInFetched, ...fetchedWithSelection];
        this.cdr.markForCheck();
    }

    private resetAndReload(): void {
        this.searchTerm = '';
        this.currentSearchTerm = '';
        this.currentStart = 1;
        this.hasMore = true;
        this.total = 0;
        this.fetchedAssets = [];
        this.displayAssets = [];

        if (this.organizationId) {
            this.loadInitialAssets();
        }
    }

    public onSearchChange(value: string): void {
        this.searchTerm = value;
        this.searchSubject.next(value);
    }

    public onScroll(event: Event): void {
        const element = event.target as HTMLElement;
        const threshold = 100;

        if (element.scrollHeight - element.scrollTop - element.clientHeight < threshold) {
            if (this.hasMore && !this.isLoadingMore && !this.isLoading) {
                this.loadMoreAssets();
            }
        }
    }

    public toggleAssetSelection(asset: AssetItem): void {
        asset.selected = !asset.selected;

        if (asset.selected) {
            this.selectedAssetsMap.set(asset.id, { ...asset, selected: true });
        } else {
            this.selectedAssetsMap.delete(asset.id);
        }

        this.cdr.markForCheck();
    }

    public onAssetSelectionChange(asset: AssetItem): void {
        if (asset.selected) {
            this.selectedAssetsMap.set(asset.id, { ...asset, selected: true });
        } else {
            this.selectedAssetsMap.delete(asset.id);
        }

        this.cdr.markForCheck();
    }

    public onApply(): void {
        const selectedAssetsList = Array.from(this.selectedAssetsMap.values());
        this.assetsSelected.emit({
            assets: selectedAssetsList,
            autoPlot: this.autoPlotOnMap
        });
        this.onClose();
    }

    public onClose(): void {
        this.initializeFromPreSelected();
        this.updateDisplayAssets();
        this.close.emit();
    }

    public clearSearch(): void {
        this.searchTerm = '';
        this.searchSubject.next('');
    }

    public resetFilter(): void {
        const hadSearchTerm = this.currentSearchTerm !== '';
        this.searchTerm = '';
        this.currentSearchTerm = '';

        if (this.organizationId && (this.fetchedAssets.length === 0 || hadSearchTerm)) {
            this.loadInitialAssets();
        }
    }

    public applyPreSelection(): void {
        this.initializeFromPreSelected();
        this.updateDisplayAssets();
    }

    public focusSearchInput(): void {
        if (this.searchInput && this.searchInput.nativeElement) {
            setTimeout(() => {
                this.searchInput.nativeElement.focus();
            }, 0);
        }
    }
}
